import type { Format, ProjectedRecord } from '@/services/projected.service'
import type { Pagination, TreeNode } from '@/types/shared.types'
import type { CampaignInstance } from './campaign-instance.service'
import { axios, sanitizeObject } from '@/lib'

export enum CampaignState {
  Active = 'Active',
  Archived = 'Archived',
  Pending = 'Pending',
  Paused = 'Paused',
  Completed = 'Completed',
  None = 'None',
}

export enum TimeUnit {
  None,
  Year,
  Quarter,
  Month,
  Day,
}

export enum CampaignKind {
  Survey = 'Survey',
  Entitlement = 'Entitlement',
  Assignment = 'Assignment',
}

export enum CampaignTimingKind {
  Once = 'Once',
  Recurring = 'Recurring',
  Change = 'Change',
}

export enum DayOfWeek {
  Sunday,
  Monday,
  Tuesday,
  Wednesday,
  Thursday,
  Friday,
  Saturday,
}

export enum AccessLevel {
  None,
  Read,
  Admin,
}

export enum ScopeType {
  Survey = 'SoftwareIDM.AccessPanel.Models.SurveyScope, SoftwareIDM.AccessPanelModel',
  Entitlement = 'SoftwareIDM.AccessPanel.Models.EntitlementScope, SoftwareIDM.AccessPanelModel',
  Change = 'SoftwareIDM.AccessPanel.Models.ChangeScope, SoftwareIDM.AccessPanelModel',
}

export const entitlementCampaignTargets = [
  {
    label: 'Identity',
    value: 'Identity',
    type: 'Reference',
  },
  {
    label: 'Resource',
    value: 'Resource',
    type: 'Reference',
  },
]

export interface CampaignTiming {
  DelayInterval?: number
  DelayUnit?: TimeUnit
  Kind: CampaignTimingKind | null
  Next?: string
  RecurrenceInterval?: number
  RecurrenceUnit?: TimeUnit
  Start: string
  StartDay: DayOfWeek | null
}

export interface CampaignScope {
  RuleFilter?: string
  Tree: TreeNode
  AdvancedFilter?: boolean
  ProjectedSilo: string | null
  IdentityIds: string[] | null
  ResourceIds: string[] | null
  Resources?: ProjectedRecord[] | null
  Identities?: ProjectedRecord[] | null
  Resource?: string | null
  Control: string[] | null
  Form: string
}

interface CampaignAction {
  status: string // TODO: define
  RevokeAccess?: boolean
  ActivityPolicyId: string
  ActivityPolicy: any // TODO: define
}

export interface CampaignDefinition {
  $type: string
  Id: string
  State: string
  Kind: string
  InitPaused: boolean
  Name: string
  Description: string
  Created: string
  OwnerIds: string[] | null
  Owners: ProjectedRecord[]
  ViewerIds: string[] | null
  Viewers: ProjectedRecord[]
  Instances: { Data: CampaignInstance[], Count: number }
  RequestPolicyId: string
  RequestPolicy: ProjectedRecord
  UserAccess: AccessLevel
  Timing: CampaignTiming
  Scope: CampaignScope
  Actions: CampaignAction
  EmailOverride: string | null
}

export interface Policy {
  Id: string
  Name: string
  Description: string
  Enabled: boolean
  PolicyType: string
  AllowDelegation: boolean
  AllowOverride: boolean
  AllowExcel: boolean
  AllowBatch: boolean
  AllowReset: boolean
  AllowExtend: boolean
}

export interface Resource {
  IdentitySilos: string[]
  Id: string
  Name: string
  Description: string
}

export interface CampaignPolicyResponse {
  AccessPolicies: Record<string, Policy[]>
  ActivityPolicies: Policy[]
  AllowCampaignDeletion: boolean
  AllowDelegations: boolean
  AllowExcel: boolean
  Forms: Policy[]
  RequestPolicies: Policy[]
  Resources: Resource[]
}

/**
 * Retrieves the campaign definitions.
 *
 * @returns {Promise<CampaignDefinitionResponse>} The campaign definitions.
 */

export interface GetCampaignDefinitionsInput extends Partial<Pagination> {
  filter?: string
}

interface GetCampaignDefinitionsResponse {
  Data: CampaignDefinition[]
  Count: number
}

export async function getCampaignDefinitions(input: GetCampaignDefinitionsInput) {
  const { data } = await axios.get<GetCampaignDefinitionsResponse>('/api/campaigndefinitions', {
    params: input,
  })

  return data
}

/**
 * Retrieves the campaign policies.
 *
 * @returns {Promise<CampaignPolicyReponse>} The campaign policies.
 */
export async function getCampaignPolicies() {
  const { data } = await axios.get<CampaignPolicyResponse>('/api/campaigndefinitions/policies')

  return data
}

interface CampaignCreationTiming {
  Kind: string
  Start: string
  RecurrenceUnit: string | null
  RecurrenceInterval: number | null
  DelayUnit: string | null
  DelayInterval: number | null
  StartDay: string | null
}

interface CampaignCreationScope {
  $type: string
  Form: string
  ProjectedSilo: string
  ObjectType: string
}

export interface CampaignCreationActions {
  Status: string
  RevokeAccess: boolean
  ActivityPolicyId: string
}

export interface CampaignCreationInput {
  $type: string
  Description: string
  EmailOverride: string | null
  Id: string
  InitPaused: boolean
  Kind: string
  Name: string
  OwnerIds: string[]
  RequestPolicyId: string
  Scope: CampaignCreationScope
  State: string
  Timing: CampaignCreationTiming
  ViewerIds: string[] | null
}

interface CampaignCreationResponse {
  Result: string
  Id: string
}

export async function createCampaignDefinition(input: any) {
  const { data } = await axios.post<CampaignCreationResponse>('/api/campaigndefinitions', input)

  return data
}

export async function updateCampaignDefinition(input: CampaignDefinition) {
  const { data } = await axios.post<CampaignCreationResponse>('/api/campaigndefinitions', input)
  return data
}

export async function deleteCampaignDefinition(definitionId: string) {
  const { data } = await axios.delete<CampaignCreationResponse>(`/api/campaigndefinitions/${definitionId}`)

  return data
}

/**
 * Translates the campaign rule tree into a human-readable format.
 *
 * @param {TreeNode} ruleTree - The campaign rule tree to be translated.
 * @returns {Promise<TranslateCampaignRuleTreeResponse>} The translated campaign rule tree.
 */

interface TranslateCampaignRuleTreeResponse {
  Result: string
  Error?: string
}

export async function translateCampaignRuleTree(ruleTree: TreeNode) {
  const { data } = await axios.post<TranslateCampaignRuleTreeResponse>(
    '/api/campaigndefinitions/translatetree',
    ruleTree,
  )

  return data
}

/**
 * Previews the campaign scope based on the provided input.
 *
 * @param {PreviewCamaignScopeInput} input - The input for previewing the campaign scope.
 * @returns {Promise<PreviewCampaignScopeResponse>} The previewed campaign scope.
 */

export interface FilterRecord {
  $type: string
  InScope: boolean
  Error: string
  Name: string
  Record: ProjectedRecord
  Identity: ProjectedRecord
  Resource: ProjectedRecord
  Control: string
}

export interface PreviewCampaignScopeInput extends Partial<Pagination> {
  CampaignType: string
  ProjectedSilo: string
  Resource: string | null
  RuleFilter?: string
  Tree?: TreeNode
}

export interface PreviewCampaignScopeResponse {
  Count: number
  Formats: Record<string, Format>
  Results: FilterRecord[]
}

export async function previewCampaignScope(input: PreviewCampaignScopeInput) {
  const filterTypeMap = {
    'Entitlement Review': 'SoftwareIDM.AccessPanel.Models.EntitlementFilter, SoftwareIDM.AccessPanelModel',
    'Survey': 'SoftwareIDM.AccessPanel.Models.ProjectedFilter, SoftwareIDM.AccessPanelModel',
  }

  const { CampaignType, term, resourceTerm, Resource, ProjectedSilo, ...inputRest } = input
  const filterType = filterTypeMap[CampaignType as keyof typeof filterTypeMap] || filterTypeMap.Survey

  const requestData = sanitizeObject({
    $type: filterType,
    ...inputRest,
    term: term ? `*${term}*` : '',
    resourceTerm: resourceTerm ? `*${resourceTerm}*` : '',
    ...(Resource ? { ResourceSilo: Resource } : {}),
    ...(ProjectedSilo ? { ProjectedSilo } : {}),
    ...(CampaignType === 'Survey' ? { simulate: true } : {}),
  })

  const { data } = await axios.post<PreviewCampaignScopeResponse>('/api/projected/filter', requestData)

  return data
}

/**
 * Retrieves campaign instances by the ID of the campaign definition.
 *
 * @param {string} definitionId - The ID of the campaign definition.
 * @returns {Promise<CampaignInstanceResponse>} The campaign instances.
 */

export interface GetCampaignInstancesByDefinitionId extends Partial<Pagination> {
  definitionId: string
}

interface CampaignInstanceResponse {
  Instances: {
    Count: number
    Data: CampaignInstance[]
  }
}

export async function getCampaignInstancesByDefinitionId(input: GetCampaignInstancesByDefinitionId) {
  const { definitionId, ...inputRest } = input

  const { data } = await axios.get<CampaignInstanceResponse>(`/api/campaigndefinitions/${definitionId}`, {
    params: {
      ...inputRest,
      sort: '-Start',
    },
  })

  return data.Instances
}

/**
 * Retrieves a campaign definition by its ID.
 *
 * @param {string} definitionId - The ID of the campaign definition.
 * @returns {Promise<CampaignDefinition>} The campaign definition.
 */
export async function getCampaignDefinition(definitionId: string) {
  const { data } = await axios.get<CampaignDefinition>(`/api/campaigndefinitions/${definitionId}`)

  return data
}
